import { useEffect, useRef, useState } from 'react';
import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from '@microsoft/signalr';
import { IOutboxEmail } from 'src/services/api/EmailOutbox/apiContracts';
import { getEmailOutboxHubUrl } from 'src/services/client';
import {
  IUseEmailOutboxSocketParams,
  OutboxSocketState,
} from 'src/pages/Outbox/types';

const OUTBOX_EMAILS_UPDATED_EVENT: string = 'outboxEmailsUpdated';

const readStoredAuthToken = (): string => {
  try {
    return JSON.parse(localStorage.getItem('auth') ?? '""');
  } catch {
    return '';
  }
};

export const useEmailOutboxSocket = (
  params: IUseEmailOutboxSocketParams
): OutboxSocketState => {
  const [socketState, setSocketState] =
    useState<OutboxSocketState>('connecting');

  // Read through a ref so a re-render with new callbacks does not tear the
  // connection down and open a new one.
  const callbacksRef = useRef<IUseEmailOutboxSocketParams>(params);
  callbacksRef.current = params;

  useEffect(() => {
    let isDisposed: boolean = false;

    const updateSocketState = (next: OutboxSocketState): void => {
      if (isDisposed) {
        return;
      }

      setSocketState(next);
    };

    // Called again on every reconnect, so a token refreshed since the page
    // loaded is the one that gets sent.
    const connection: HubConnection = new HubConnectionBuilder()
      .withUrl(getEmailOutboxHubUrl(), {
        accessTokenFactory: readStoredAuthToken,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    connection.on(OUTBOX_EMAILS_UPDATED_EVENT, (emails: IOutboxEmail[]) => {
      callbacksRef.current.onEmailsUpdated(emails);
    });

    connection.onreconnecting(() => {
      updateSocketState('reconnecting');
    });

    // Pushes sent while disconnected are lost, so the list has to be refetched.
    connection.onreconnected(() => {
      updateSocketState('live');
      callbacksRef.current.onReconnected();
    });

    connection.onclose(() => {
      updateSocketState('offline');
    });

    const startConnection = async (): Promise<void> => {
      try {
        await connection.start();
        updateSocketState('live');
      } catch (error) {
        console.error('Email outbox live updates failed to connect:', error);
        updateSocketState('offline');
      }
    };

    void startConnection();

    return () => {
      isDisposed = true;
      void connection.stop();
    };
  }, []);

  return socketState;
};
